<div [ngClass]="order?.location?.isOpen ? 'w-100' : ''" role="region" aria-label="Order ready time selection">
    <div class="d-flex flex-row flex-nowrap position-relative" role="tablist">
        <ng-container *ngIf="!order?.location?.isOpen">
            <div class="d-flex flex-column w-100 mb-2" id="location-closed" role="alert" aria-live="polite">
                <ng-container *ngIf="!order.location.isOpen && order?.location?.supportsAdvancedOrders">
                    <h5 class="mb-2 fw-bold header-font text-capitalize">
                        Restaurant is Currently Closed
                    </h5>
                    <div>
                        You can still schedule your order ahead for when it is open, or try a different location.
                    </div>
                </ng-container>
            </div>
        </ng-container>

        <ng-container *ngIf="order?.location?.supportsAsapOrders && !order?.location?.supportsAdvancedOrders">
            <ng-container *ngIf="order?.location?.isOpen">
                <div class="d-flex align-items-center w-100" style="height: 38px" aria-live="polite">
                    <span id="ready-time-label">Ready at:</span>&nbsp;<b aria-labelledby="ready-time-label">{{
                    order?.earliestReadyTimestamp | date: 'M/d/yy h:mm a' : order.location.utcOffset
                    }}</b>
                </div>
            </ng-container>
        </ng-container>
    </div>

    <ng-container *ngIf="order?.location?.supportsAsapOrders || order?.location?.supportsAdvancedOrders">
        <ng-container *ngIf="actualTimes; else loadingTimes">
            <form [formGroup]="dateTimeForm">
                <!-- Date Selection Section -->
                <div class="d-flex flex-column flex-grow-1 mb-2">
                    <ng-container *ngIf="!order?.location?.isOpen && order?.location?.supportsAdvancedOrders">
                        <h5 class="fw-bold header-font flex-grow-1 mb-2" id="schedule-ahead-heading">
                            Schedule Ahead
                        </h5>
                    </ng-container>

                    <div class="d-flex flex-row flex-nowrap align-items-center justify-content-between mb-2">
                        <div class="flex-grow-1 d-flex flex-column">
                            <h6 class="header-font fw-bold p-0 m-0 d-flex flex-row flex-nowrap" id="date-selection-heading">
                                Select a Date
                                <div class="de-btn-mini-loader btn-loader m-0 ms-2" *ngIf="dateLoading" aria-hidden="true"></div>
                            </h6>
                        </div>
                    </div>

                    <!-- Button style date selection for first 4 dates -->
                    <div class="date-buttons-grid mb-3">
                        <ng-container *ngFor="let date of availableDates | slice:0:4; trackBy: availableDateTracker; let i = index">
                            <button
                                    type="button"
                                    class="w-100 text-nowrap d-flex flex-column align-items-center justify-content-center"
                                    [class.de-primary-btn]="isDateSelected(date)"
                                    [class.de-primary-btn-outline]="!isDateSelected(date)"
                                    [disabled]="dateLoading"
                                    (click)="onDateButtonClick(date)"
                                    [attr.aria-pressed]="isDateSelected(date)"
                                    [attr.aria-label]="'Select ' + (formatShortDate(date))">
                                <span class="fw-bold">{{ formatShortDate(date) }}</span>
                                <small class="font-size-xsmall fw-normal">{{ isToday(date) ? 'Today' : formatWeekday(date) }}</small>
                            </button>
                        </ng-container>
                    </div>

                    <!-- Dropdown for additional dates -->
                    <div class="w-100 mb-4" *ngIf="availableDates.length > 4">
                        <select
                                class="form-select"
                                aria-labelledby="date-selection-heading"
                                (change)="onDateSelectChange($event)"
                                [disabled]="dateLoading"
                                [value]="dropdownDateSelection">
                            <option value="" disabled [selected]="!dropdownDateSelection">More Dates</option>
                            <ng-container *ngFor="let date of availableDates | slice:4; trackBy: availableDateTracker">
                                <option [value]="date.toISOString()" [selected]="date.toISOString() === dropdownDateSelection">
                                    {{ formatShortDate(date) }} {{ isToday(date) ? 'Today' : formatWeekday(date) }}
                                </option>
                            </ng-container>
                        </select>
                    </div>

                </div>

                <!-- Time Selection Section -->
                <div class="d-flex flex-column flex-grow-1">
                    <div class="d-flex flex-row justify-content-between align-items-center mb-2">
                        <div class="flex-grow-1 d-flex flex-column">
                            <h6 class="header-font fw-bold p-0 m-0 d-flex flex-row flex-nowrap" id="time-selection-heading">
                                Select a Time
                                <div class="de-btn-mini-loader btn-loader m-0 ms-2" *ngIf="timeLoading" aria-hidden="true"></div>
                            </h6>
                        </div>
                    </div>

                    <div class="w-100 mb-2">
                        <ng-container *ngIf="actualTimes.length > 0; else noTimes">
                            <div class="d-flex flex-column w-100">
                                <!-- Time buttons grid (ASAP + first 3 times or first 4 times) -->
                                <div class="time-buttons-grid mb-3">
                                    <!-- ASAP Option -->
                                    <ng-container *ngIf="order?.location?.supportsAsapOrders && order?.location?.isOpen && (!selectedDate || (selectedDate | timeComparison: order.earliestReadyTimestamp : 'day': order.location.timezone)) && (order.earliestReadyTimestamp | timeComparison: today : 'day': order.location.timezone)">
                                        <button
                                                type="button"
                                                class="d-flex align-items-center justify-content-center flex-column"
                                                [ngClass]="{'de-primary-btn': order.isASAP, 'de-primary-btn-outline': !order.isASAP}"
                                                [disabled]="timeLoading || !order?.location?.isOpen"
                                                (click)="asapClicked()"
                                                aria-label="As soon as possible">
                                            <ng-container *ngIf="loadingTimeMode !== 'asap'">
                                                <span class="fw-bold">ASAP</span>
                                                <small class="d-block font-size-xsmall fw-normal" aria-label="Today at {{ order.earliestReadyTimestamp | date: 'shortTime' : order.location.utcOffset }}">
                                                    Today {{ order.earliestReadyTimestamp | date: 'shortTime' : order.location.utcOffset }}
                                                </small>
                                            </ng-container>
                                            <ng-container *ngIf="loadingTimeMode === 'asap'">
                                                <div class="spinner-border spinner-border-sm" role="status">
                                                    <span class="visually-hidden">Loading...</span>
                                                </div>
                                            </ng-container>
                                        </button>
                                    </ng-container>

                                    <!-- Regular time buttons -->
                                    <ng-container *ngFor="let time of getTimeButtonsToShow(); trackBy: availableTimeTracker">
                                        <ng-container *ngIf="!(time | timeComparison : order.earliestReadyTimestamp : 'minute': order.location.timezone)">
                                            <button
                                                    type="button"
                                                    class="w-100 text-nowrap text-uppercase"
                                                    [class.de-primary-btn]="isTimeSelected(time)"
                                                    [class.de-primary-btn-outline]="!isTimeSelected(time)"
                                                    [disabled]="timeLoading"
                                                    (click)="onTimeButtonClick(time)"
                                                    [attr.aria-pressed]="isTimeSelected(time)"
                                                    [attr.aria-label]="'Select time ' + (formatTime(time))">
                                                {{ formatTime(time) }}
                                            </button>
                                        </ng-container>
                                    </ng-container>
                                </div>

                                <!-- Time Dropdown for additional times -->
                                <div *ngIf="actualTimes.length > getTimeButtonsLimit()">
                                    <select
                                            class="form-select"
                                            aria-labelledby="time-selection-heading"
                                            (change)="onTimeSelectChange($event)"
                                            [disabled]="timeLoading"
                                            [value]="dropdownTimeSelection">
                                        <option value="" disabled [selected]="!dropdownTimeSelection">More Times</option>
                                        <ng-container *ngFor="let time of actualTimes | slice:getTimeButtonsLimit(); trackBy: availableTimeTracker">
                                            <ng-container *ngIf="!(time | timeComparison : order.earliestReadyTimestamp : 'minute': order.location.timezone)">
                                                <option class="text-uppercase" [value]="time.toISOString()" [selected]="time.toISOString() === dropdownTimeSelection">
                                                    {{ formatTime(time) }}
                                                </option>
                                            </ng-container>
                                        </ng-container>
                                    </select>
                                </div>
                            </div>
                        </ng-container>
                    </div>
                </div>
            </form>
        </ng-container>
    </ng-container>

    <ng-template #noTimes>
        <div
                id="no-times"
                [attr.data-open]="order.location.isOpen"
                class="alert alert-warning w-100 text-center de-card-radius px-3 py-2 m-0"
                role="alert">
            There are no more times available for this date, please select another day.
        </div>
    </ng-template>

    <ng-template #loadingTimes>
        <div
                class="card d-flex align-items-center justify-content-center flex-column w-100 text-center de-card-radius p-4 m-0"
                role="status"
                aria-live="polite">
            <div class="item mb-2">
                <div class="item-inner">
                    <div class="item-loader-container">
                        <div class="la-ball-pulse-sync" aria-hidden="true">
                            <div></div>
                            <div></div>
                            <div></div>
                        </div>
                    </div>
                </div>
            </div>
            <div>Getting Available Dates and Times</div>
            <span class="visually-hidden">Loading available dates and times</span>
        </div>
    </ng-template>
</div>